import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useProject } from "../ProjectContext";

const WorkflowSidebar = ({ isOpen, setIsOpen }) => {
  const { activeProject } = useProject();
  const location = useLocation();
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  // Define workflow stages with their corresponding database stage names
  const stages = [
    { path: "/project/landing", label: "Upload", icon: "📤", stageName: "ready" },
    { path: "/project/embedding", label: "Embedding", icon: "📊", stageName: "embedding" },
    { path: "/project/umap", label: "UMAP", icon: "🗺️", stageName: "umap" },
    { path: "/project/pca", label: "PCA", icon: "📈", stageName: "pca" },
    { path: "/project/cluster", label: "Cluster", icon: "🎯", stageName: "clustering" },
  ];

  // Get current stage index (for progress calculation)
  const currentIndex = stages.findIndex(s => s.path === location.pathname);
  const isOnDashboard = location.pathname === "/dashboard" || location.pathname === "/ProjectDashboard";

  // Get current stage from activeProject
  const currentStage = activeProject?.currentStage || "init";

  // Stage order for comparison
  const stageOrder = {
    "init": 0,
    "ready": 1,
    "embedding": 2,
    "umap": 3,
    "pca": 3, // UMAP and PCA are at same level
    "clustering": 4,
    "cosine_similarity": 2, // Optional, doesn't block progression
    "end": 5
  };

  // Helper function to check if a stage is completed based on currentStage
  const isStageCompleted = (stageStageName) => {
    const currentOrder = stageOrder[currentStage] || 0;
    const stageOrderValue = stageOrder[stageStageName] || 0;
    console.log(currentOrder, stageOrderValue);
    console.log(currentStage);
    return currentOrder >= stageOrderValue;
  };

  return (
    <>
      {/* Desktop Sidebar - Sticky positioning stays in flex layout */}
      <aside className="hidden md:block sticky top-0 left-0 w-80 h-full max-h-full bg-white border-r-2 border-[#dee2e6] shadow-[4px_0_12px_rgba(0,0,0,0.08)] px-4 py-6 font-['Montserrat'] overflow-y-auto">

        {/* Project Dashboard Section */}
        <div className="mb-8">
          <Link
            to="/project"
            onClick={() => setIsOpen(false)}
            className={`
              flex items-center gap-3 px-4 py-3 rounded-lg
              border-2 transition-all duration-300 no-underline
              ${isOnDashboard
                ? 'bg-gradient-to-br from-[#fff5f5] to-[#ffe6e6] border-[#f53838] shadow-[0_4px_12px_rgba(245,56,56,0.2)]'
                : 'bg-white border-transparent hover:border-[#f53838] hover:shadow-[0_2px_8px_rgba(245,56,56,0.15)]'
              }
            `}
          >
            <span className="text-2xl">📁</span>
            <span className={`text-lg font-bold flex-1 ${isOnDashboard ? 'text-[#f53838]' : 'text-[#212529]'}`}>
              Projects
            </span>
          </Link>
        </div>

        {/* Workflow Pipeline Section - Only show if project is active */}
        {activeProject && (
          <div>
            <div className="mb-6 pb-4 border-b-2 border-[#dee2e6]">
              <h3 className="m-0 mb-1 text-xl font-bold text-[#212529]">
                Workflow Pipeline
              </h3>
              <p className="m-0 text-[13px] text-[#6c757d] font-medium">
                {activeProject.projectName || activeProject.name}
              </p>
            </div>

            {/* Timeline */}
            <div className="relative pl-2">
              {stages.map((stage, index) => {
                const isActive = location.pathname === stage.path;
                const isCompleted = isStageCompleted(stage.stageName);
                
                // Determine accessibility based on currentStage
                let isAccessible = false;
                const currentOrder = stageOrder[currentStage] || 0;
                
                if (stage.path === "/project/landing") {
                  // Upload is always accessible
                  isAccessible = true;
                } else if (stage.path === "/project/embedding") {
                  // Embedding is accessible if stage is at least "ready" (file uploaded)
                  // Stage will be "ready" after file upload, "embedding" after embeddings are generated
                  isAccessible = currentOrder >= stageOrder["ready"];
                } else if (stage.path === "/project/umap") {
                  // UMAP requires embedding stage (stage >= embedding)
                  isAccessible = currentOrder >= stageOrder["embedding"];
                } else if (stage.path === "/project/pca") {
                  // PCA requires embedding stage (stage >= embedding)
                  isAccessible = currentOrder >= stageOrder["embedding"];
                } else if (stage.path === "/project/cluster") {
                  // Clustering requires either UMAP or PCA stage
                  // Stage will be "umap" or "pca" after respective reduction is done
                  isAccessible = currentStage === "umap" || currentStage === "pca" || currentOrder >= stageOrder["clustering"];
                }

                return (
                  <Link
                    key={stage.path}
                    to={stage.path}
                    onClick={(e) => {
                      if (!isAccessible) {
                        e.preventDefault();
                        return;
                      }
                      setIsOpen(false);
                    }}
                    className={`
                      flex items-start relative py-3 no-underline
                      transition-all duration-300 ease-in-out
                      ${isAccessible ? 'hover:translate-x-1' : ''}
                      ${!isAccessible ? 'opacity-40 cursor-not-allowed' : ''}
                    `}
                  >
                    {/* Timeline Marker */}
                    <div className="relative flex flex-col items-center mr-4">
                      <div className={`
                        relative w-10 h-10 rounded-full
                        flex items-center justify-center
                        text-base font-bold
                        transition-all duration-300 ease-in-out
                        z-[2] border-[3px]
                        ${isActive 
                          ? 'bg-gradient-to-br from-[#f53838] to-[#d40000] border-[#f53838] text-white shadow-[0_4px_12px_rgba(245,56,56,0.4)] scale-110' 
                          : isCompleted
                          ? 'bg-[#28a745] border-[#28a745] text-white'
                          : 'bg-white border-[#adb5bd] text-[#6c757d]'
                        }
                      `}>
                        {isCompleted ? "✓" : index + 1}
                      </div>

                      {index < stages.length - 1 && (
                        <div className={`
                          absolute top-10 left-1/2 -translate-x-1/2
                          w-[3px] h-12
                          transition-colors duration-300
                          ${isActive 
                            ? 'bg-gradient-to-b from-[#f53838] to-[#dee2e6]'
                            : isCompleted ? 'bg-[#28a745]' : 'bg-[#dee2e6]'
                          }
                        `} />
                      )}
                    </div>

                    {/* Timeline Content */}
                    <div className={`
                      flex flex-col gap-2 flex-1
                      px-4 py-2 rounded-lg
                      border-2 transition-all duration-300
                      ${isActive
                        ? 'bg-gradient-to-br from-[#fff5f5] to-[#ffe6e6] border-[#f53838] shadow-[0_4px_12px_rgba(245,56,56,0.2)]'
                        : isCompleted
                        ? 'bg-[#f1f8f4] border-[#28a745]'
                        : 'bg-white border-transparent hover:border-[#f53838] hover:shadow-[0_2px_8px_rgba(245,56,56,0.15)]'
                      }
                    `}>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{stage.icon}</span>
                        <span className={`
                          text-base font-semibold flex-1
                          ${isActive ? 'text-[#f53838]' : 'text-[#212529]'}
                        `}>
                          {stage.label}
                        </span>
                      </div>
                      {isActive && (
                        <span className="text-[11px] px-2 py-0.5 bg-[#f53838] text-white rounded-xl font-semibold uppercase tracking-wide self-start">
                          Current
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Progress Footer */}
            <div className="mt-8 pt-4 border-t-2 border-[#dee2e6]">
              <div className="flex justify-between items-center px-4 py-3 bg-white rounded-lg text-sm text-[#6c757d]">
                <span>Progress</span>
                <strong className="text-lg text-[#f53838] font-bold">
                  {currentIndex >= 0 ? currentIndex + 1 : 0} / {stages.length}
                </strong>
              </div>
            </div>
          </div>
        )}

        {/* Message when no project is active */}
        {!activeProject && (
          <div className="text-center py-8 text-[#6c757d]">
            <p className="text-sm">Select a project to begin your workflow</p>
          </div>
        )}
      </aside>

      {/* Mobile Accordion - Collapsible at top of page */}
      {activeProject && (
        <div className="md:hidden bg-white border-b-2 border-[#dee2e6] shadow-sm mb-4">
          <button
            onClick={() => setIsAccordionOpen(!isAccordionOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold text-[#212529]">Workflow Pipeline</span>
              {activeProject && (
                <span className="text-sm text-[#6c757d]">({activeProject.projectName || activeProject.name})</span>
              )}
            </div>
            {isAccordionOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          {isAccordionOpen && (
            <div className="px-4 pb-4 border-t border-[#dee2e6]">
              {/* Projects Link */}
              <Link
                to="/project"
                onClick={() => setIsAccordionOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg mb-4
                  border-2 transition-all duration-300 no-underline
                  ${isOnDashboard
                    ? 'bg-gradient-to-br from-[#fff5f5] to-[#ffe6e6] border-[#f53838] shadow-[0_4px_12px_rgba(245,56,56,0.2)]'
                    : 'bg-white border-transparent hover:border-[#f53838] hover:shadow-[0_2px_8px_rgba(245,56,56,0.15)]'
                  }
                `}
              >
                <span className="text-2xl">📁</span>
                <span className={`text-base font-bold flex-1 ${isOnDashboard ? 'text-[#f53838]' : 'text-[#212529]'}`}>
                  Projects
                </span>
              </Link>

              {/* Timeline - Vertical stack on mobile */}
              <div className="space-y-2">
                {stages.map((stage, index) => {
                  const isActive = location.pathname === stage.path;
                  const isCompleted = isStageCompleted(stage.stageName);
                  
                  // Determine accessibility based on currentStage
                  let isAccessible = false;
                  const currentOrder = stageOrder[currentStage] || 0;
                  
                  if (stage.path === "/project/landing") {
                    isAccessible = true;
                  } else if (stage.path === "/project/embedding") {
                    isAccessible = currentOrder >= stageOrder["ready"];
                  } else if (stage.path === "/project/umap") {
                    isAccessible = currentOrder >= stageOrder["embedding"];
                  } else if (stage.path === "/project/pca") {
                    isAccessible = currentOrder >= stageOrder["embedding"];
                  } else if (stage.path === "/project/cluster") {
                    isAccessible = currentStage === "umap" || currentStage === "pca" || currentOrder >= stageOrder["clustering"];
                  }

                  return (
                    <Link
                      key={stage.path}
                      to={stage.path}
                      onClick={(e) => {
                        if (!isAccessible) {
                          e.preventDefault();
                          return;
                        }
                        setIsAccordionOpen(false);
                      }}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-lg
                        border-2 transition-all duration-300 no-underline
                        ${isActive
                          ? 'bg-gradient-to-br from-[#fff5f5] to-[#ffe6e6] border-[#f53838] shadow-[0_4px_12px_rgba(245,56,56,0.2)]'
                          : isCompleted
                          ? 'bg-[#f1f8f4] border-[#28a745]'
                          : 'bg-white border-transparent hover:border-[#f53838] hover:shadow-[0_2px_8px_rgba(245,56,56,0.15)]'
                        }
                        ${!isAccessible ? 'opacity-40 cursor-not-allowed' : ''}
                      `}
                    >
                      <div className="flex flex-col gap-2 flex-1">
                        <div className="flex items-center gap-3">
                          <div className={`
                            w-8 h-8 rounded-full flex items-center justify-center
                            text-sm font-bold border-2
                            ${isActive 
                              ? 'bg-gradient-to-br from-[#f53838] to-[#d40000] border-[#f53838] text-white' 
                              : isCompleted
                              ? 'bg-[#28a745] border-[#28a745] text-white'
                              : 'bg-white border-[#adb5bd] text-[#6c757d]'
                            }
                          `}>
                            {isCompleted ? "✓" : index + 1}
                          </div>
                          <span className="text-xl">{stage.icon}</span>
                          <span className={`text-sm font-semibold flex-1 ${isActive ? 'text-[#f53838]' : 'text-[#212529]'}`}>
                            {stage.label}
                          </span>
                        </div>
                        {isActive && (
                          <span className="text-[10px] px-2 py-0.5 bg-[#f53838] text-white rounded-xl font-semibold uppercase self-start ml-11">
                            Current
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* Progress Footer */}
              <div className="mt-4 pt-4 border-t-2 border-[#dee2e6]">
                <div className="flex justify-between items-center px-4 py-2 bg-white rounded-lg text-xs text-[#6c757d]">
                  <span>Progress</span>
                  <strong className="text-base text-[#f53838] font-bold">
                    {currentIndex >= 0 ? currentIndex + 1 : 0} / {stages.length}
                  </strong>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default WorkflowSidebar;